import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import editorStore from '../../stores/EditorStore';

const C = {
  workspace: '#12141c',
  border:    'rgba(255,255,255,0.07)',
  text2:     '#9da3c8',
  text3:     '#5c6290',
  accent:    '#5b79ff',
  accentGlow:'rgba(91,121,255,0.35)',
  surface2:  '#1c1e2e',
};

const CanvasWorkspace = observer(() => {
  const canvasRef   = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;
    editorStore.initCanvas(canvasRef.current, containerRef.current);

    // Keep Fabric canvas background transparent on every render so the dark workspace shows through.
    // Page switches restore backgroundColor from JSON; this guard re-applies transparency after each render.
    const fc = editorStore.getCanvas();
    if (fc) {
      (fc as any).backgroundColor = '';
      fc.requestRenderAll();
      fc.on('after:render', () => {
        if ((fc as any).backgroundColor !== '') {
          (fc as any).backgroundColor = '';
          fc.requestRenderAll();
        }
      });
    }


    const ro = new ResizeObserver(() => editorStore.fitCanvasToContainer());
    ro.observe(containerRef.current);
    return () => { ro.disconnect(); editorStore.destroy(); };
  }, []);

  useEffect(() => {
    // Attach to containerRef, not canvasRef: Fabric.js creates an upperCanvas on top of the lower
    // canvas that captures all pointer/wheel events — the lower canvas (canvasRef) never receives them.
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // Only zoom while the cursor is over the page, not the surrounding workspace
      const rect = el.getBoundingClientRect();
      if (!editorStore.isScreenPointOnPage(e.clientX - rect.left, e.clientY - rect.top)) return;
      e.preventDefault();
      const active = editorStore.getCanvas()?.getActiveObject();
      if (active && (active as any).customType === 'frameImage') {
        editorStore.zoomFrameImage(active, e.deltaY);
      } else {
        editorStore.zoomAtPoint(e.offsetX, e.offsetY, e.deltaY);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const active = editorStore.getCanvas()?.getActiveObject();
      if (active && (active as any).isEditing) return;
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); editorStore.deleteSelected(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); e.shiftKey ? editorStore.history.redo() : editorStore.history.undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); editorStore.history.redo(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('application/x-image-item');
    if (!data || !canvasRef.current) return;
    const imageItem = JSON.parse(data) as { id: number; src: string };
    const rect = canvasRef.current.getBoundingClientRect();
    const vt   = editorStore.getCanvas()?.viewportTransform || [1, 0, 0, 1, 0, 0];
    const zoom = vt[0];
    const canvasX = (e.clientX - rect.left - vt[4]) / zoom;
    const canvasY = (e.clientY - rect.top  - vt[5]) / zoom;

    // Priority: place inside a frame if one is under the cursor or currently selected.
    // This makes the image a frameImage (customType='frameImage') so scroll-to-zoom works.
    const frame = editorStore.getFrameAtPoint(canvasX, canvasY) ?? editorStore.getActiveFrame();
    if (frame) {
      editorStore.placeImageInFrame(imageItem.src, frame);
      return;
    }

    // No frame — add as a free image scaled to fit the canvas.
    const img = new Image();
    img.src = imageItem.src;
    img.onload = () => {
      const scale  = Math.min(editorStore.width / img.width, editorStore.height / img.height);
      const finalW = img.width  * scale;
      const finalH = img.height * scale;
      editorStore.activePage.addElement({
        type: 'image', src: imageItem.src,
        width: finalW, height: finalH,
        x: canvasX - finalW / 2, y: canvasY - finalH / 2,
      });
    };
  };

  const zoomPct = Math.round(editorStore.scale * 100);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%', height: '100%',
        position: 'relative', overflow: 'hidden',
        background: C.workspace,
        backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Hint */}
      <div style={{
        position: 'absolute', top: 12, right: 14, zIndex: 10, pointerEvents: 'none',
        background: 'rgba(28,30,46,0.88)', border: `1px solid ${C.border}`,
        padding: '5px 10px', borderRadius: 6, fontSize: 11, color: C.text3,
        display: 'flex', alignItems: 'center', gap: 5,
      }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        Double-click image to adjust · <span style={{ color: C.accent }}>Alt+drag</span> to pan
      </div>

      {/* Fabric canvas host */}
      <div style={{ position: 'absolute', inset: 0 }}>
        <canvas ref={canvasRef} />
      </div>

      {/* Zoom controls */}
      <div style={{
        position: 'absolute', bottom: 16, right: 16,
        display: 'flex', alignItems: 'center',
        background: C.surface2, border: `1px solid ${C.border}`,
        borderRadius: 10, overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        userSelect: 'none', zIndex: 10,
      }}>
        <ZoomBtn onClick={() => editorStore.zoom(1 / 1.25)} title="Zoom out">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/>
          </svg>
        </ZoomBtn>
        <div style={{ width: 1, height: 18, background: C.border }} />
        <button
          onClick={() => editorStore.fitCanvasToContainer()}
          style={{
            padding: '0 12px', height: 34, background: 'none', border: 'none',
            fontSize: 11, fontWeight: 600, color: C.text2, cursor: 'pointer',
            minWidth: 52, textAlign: 'center',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          title="Fit to screen"
        >
          {zoomPct}%
        </button>
        <div style={{ width: 1, height: 18, background: C.border }} />
        <ZoomBtn onClick={() => editorStore.zoom(1.25)} title="Zoom in">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
          </svg>
        </ZoomBtn>
        <div style={{ width: 1, height: 18, background: C.border }} />
        <ZoomBtn onClick={() => editorStore.fitCanvasToContainer()} title="Fit canvas">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
          </svg>
        </ZoomBtn>
      </div>
    </div>
  );
});

const ZoomBtn: React.FC<{ onClick: () => void; title: string; children: React.ReactNode }> = ({ onClick, title, children }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      width: 34, height: 34, background: 'none', border: 'none',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#9da3c8',
    }}
    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
  >
    {children}
  </button>
);

export default CanvasWorkspace;
